import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "./button";
import { XIcon } from "./XIcon";

const alertVariants = cva(
  "relative rounded-md p-3 text-sm inline-flex items-center justify-between gap-2",
  {
    variants: {
      variant: {
        default: "",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        minimal: "bg-transparent p-0",
      },
      colors: {
        default: "",
        error: "",
        alert: "",
        success: "",
        link: "",
      },
    },
    compoundVariants: [
      // Default variant with colors
      {
        variant: "default",
        colors: "default",
        className: "bg-bg-secondary text-text-primary",
      },
      {
        variant: "default",
        colors: "error",
        className:
          "bg-semantic-error/20 text-text-primary [&>svg]:text-semantic-error",
      },
      {
        variant: "default",
        colors: "alert",
        className:
          "bg-semantic-alert/20 text-text-primary [&>svg]:text-semantic-alert",
      },
      {
        variant: "default",
        colors: "success",
        className:
          "bg-semantic-success/20 text-text-primary [&>svg]:text-semantic-success",
      },
      {
        variant: "default",
        colors: "link",
        className:
          "bg-semantic-link/20 text-text-primary [&>svg]:text-semantic-link",
      },

      // Minimal variant with colors
      {
        variant: "minimal",
        colors: "default",
        className: "text-text-primary [&>svg]:text-text-primary",
      },
      {
        variant: "minimal",
        colors: "error",
        className: "text-semantic-error [&>svg]:text-semantic-error",
      },
      {
        variant: "minimal",
        colors: "alert",
        className: "text-semantic-alert [&>svg]:text-semantic-alert",
      },
      {
        variant: "minimal",
        colors: "success",
        className: "text-semantic-success [&>svg]:text-semantic-success",
      },
      {
        variant: "minimal",
        colors: "link",
        className: "text-semantic-link [&>svg]:text-semantic-link",
      },
    ],
    defaultVariants: {
      variant: "default",
      colors: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, colors, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(
      alertVariants({ variant, colors }),
      className,
      "items-center",
    )}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col", className)} {...props} />
));
AlertContent.displayName = "AlertContent";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      "text-md mb-1 font-semibold leading-none tracking-tight text-inherit",
      className,
    )}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertAction = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button ref={ref} className={cn(className)} {...props} />
  ),
);
AlertAction.displayName = "AlertAction";

const AlertIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(className)} {...props} />
));
AlertIcon.displayName = "AlertIcon";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-sm font-medium text-text-secondary [&_p]:leading-relaxed",
      className,
    )}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

const AlertClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, onClick, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      "cursor-pointer bg-bg-secondary border border-stroke-subtle",
      "flex items-center justify-center p-0.5 rounded-full",
      "absolute -top-1 -left-1",
      className,
    )}
    onClick={(e) => {
      const alertElement = e.currentTarget.closest('[role="alert"]');
      if (alertElement) {
        alertElement.remove();
      }
      onClick?.(e);
    }}
    {...props}
  >
    <XIcon size={12} />
  </button>
));
AlertClose.displayName = "AlertClose";

export {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
  AlertContent,
  AlertIcon,
  AlertClose,
};
